import type { FC, PropsWithChildren } from 'react';
import { MDText } from 'i18n-react';
import { useRouter } from 'next/router';

import { Button } from '@motorway/mw-highway-code';

import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { ACCOUNT_SETTINGS_COMPANY_INFORMATION_LINK } from 'Components/pages/AccountSettings/AccountSettings.consts';
import {
	triggerAccountEmptyViewNoResultsCTAClick,
	triggerAccountErrorViewRefreshCTAClick,
	triggerAccountPageEmptyViewResetFiltersCTAClick,
} from 'Services/analytics/events';
import { applyCypressData } from 'Utilities/helpers';
import useVehicleListButtonConfig from 'Utilities/hooks/vehicleList/useVehicleListButtonConfig';
import ROUTES from 'Utilities/routes';

import PageTexts from '../../AccountPage.texts';

import LocalTexts from './VehicleListEmptyState.texts';

import styles from './VehicleListEmptyState.module.scss';

const LocalT = new MDText(LocalTexts);
const PagesT = new MDText(PageTexts);

export enum VehicleListEmptyStateVariant {
	Empty = 'empty',
	EmptyKyc = 'emptyKyc',
	EmptyKycSubmitted = 'emptyKycSubmitted',
	Error = 'error',
	Filters = 'filters',
}

type VehicleListEmptyProps = PropsWithChildren<{
	variant?: VehicleListEmptyStateVariant;
}>;

const VehicleListEmptyState: FC<VehicleListEmptyProps> = ({ variant = VehicleListEmptyStateVariant.Empty }) => {
	const marketplaceButtonConfig = useVehicleListButtonConfig();
	const { pathname, push, reload } = useRouter();
	const { category, slug } = useAccountRouter();

	const Options = {
		[VehicleListEmptyStateVariant.EmptyKycSubmitted]: {
			button: '',
			description: 'emptyKycSubmitted.description',
			onClick: () => {
				void push(ACCOUNT_SETTINGS_COMPANY_INFORMATION_LINK);
			},
			title: 'emptyKyc.title',
		},
		[VehicleListEmptyStateVariant.EmptyKyc]: {
			button: 'emptyKyc.button',
			description: 'emptyKyc.description',
			onClick: () => {
				void push(ACCOUNT_SETTINGS_COMPANY_INFORMATION_LINK);
			},
			title: 'emptyKyc.title',
		},
		[VehicleListEmptyStateVariant.Empty]: {
			button: 'empty.button',
			description: 'empty.description',
			onClick: () => {
				void push(marketplaceButtonConfig.href, marketplaceButtonConfig.as, { shallow: true });
				triggerAccountEmptyViewNoResultsCTAClick({
					buttonLabel: LocalT.translate('empty.button'),
					slug,
					url: ROUTES.VEHICLE_LIST.as(),
				});
			},
			title: 'empty.title',
		},
		[VehicleListEmptyStateVariant.Filters]: {
			button: 'filters.button',
			description: 'filters.description',
			onClick: () => {
				void push(
					{
						pathname,
						query: {
							params: [category, slug],
						},
					},
					undefined,
					{ shallow: true },
				);
				triggerAccountPageEmptyViewResetFiltersCTAClick({
					buttonLabel: LocalT.translate('filters.button'),
					slug,
					url: ROUTES.ACCOUNT.as(category, slug),
				});
			},
			title: 'filters.title',
		},
		[VehicleListEmptyStateVariant.Error]: {
			button: 'error.button',
			description: 'error.description',
			onClick: () => {
				reload();
				triggerAccountErrorViewRefreshCTAClick({
					buttonLabel: LocalT.translate('error.button'),
					slug,
					url: ROUTES.ACCOUNT.as(category, slug),
				});
			},
			title: 'error.title',
		},
	};

	const { button, description, onClick, title } = Options[variant];

	return (
		<div className={styles.container}>
			<div>
				<h3 className={styles.header} role="status" {...applyCypressData(cypressIds.accountDataGrid.emptyState.header)}>
					{LocalT.translate(title, {
						slug: PagesT.translate(`pages.${slug}`),
					})}
				</h3>
				<p className={styles.description}>{LocalT.translate(description)}</p>
				{!!button && (
					<Button
						{...applyCypressData(cypressIds.buttons.purchasesEmptyState)}
						label={LocalT.translate(button)}
						onClick={onClick}
						size="medium"
						variant="primary"
					/>
				)}
			</div>
		</div>
	);
};

export { VehicleListEmptyState };
